import { type PropsWithChildren } from 'react';

import { units } from '@dynatrace-sdk/units';

import { useGridPlotSize } from '../../core/components/chart-grid/hooks/useGridPlotSize.js';
import type {
  FormatterWithPrecision,
  StringFormatter,
} from '../../core/types/formatter.js';
import type { TickAxis } from '../../core/utils/formatter-by-ticks.js';
import { getFormatterByTicks } from '../../core/utils/formatter-by-ticks.js';
import type { ContinuousScale } from '../../core/utils/has-horizontal-label-collision.js';
import { safeGet } from '../../core/utils/safe-get.js';
import {
  getHorizontalNumTicks,
  getVerticalNumTicks,
} from '../../core/utils/ticks.js';
import { XYChartAxesTicksContext } from '../context/axes/XYChartAxesTicks.context.js';
import { useAxesFormatter } from '../hooks/useAxesFormatter.js';
import {
  type FormatCategory,
  useFormatCategory,
} from '../hooks/useFormatCategory.js';
import { useVisibleAxesConfig } from '../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../hooks/useVisibleAxesScales.js';
import { isCategorical } from '../types/guards.js';
import type {
  AnyXYChartScale,
  AxisIdToFormatter,
  XYChartAxisInternal,
} from '../types/xy-chart-internals.js';
import { filterOutOfRangeTicks } from '../utils/filter-out-of-range-ticks.js';
import {
  computeNiceUnitTicks,
  shouldUseNiceUnitTicks,
} from '../utils/generate-nice-unit-ticks.js';
import { getTicksFormattedByConfig } from '../utils/get-ticks-formatted-by-config.js';

function getFormatter(
  numTicks: number,
  axisIdToFormatter: AxisIdToFormatter,
  config: XYChartAxisInternal,
  scale: AnyXYChartScale | undefined,
  formatCategory: FormatCategory,
) {
  if (isCategorical(config.type)) {
    const stringFormatter: StringFormatter = (raw) =>
      formatCategory(config, raw) ?? raw;
    return stringFormatter;
  }
  return config?.isRelative
    ? getFormatterByTicks(numTicks, scale, units.percentage.percent)
    : safeGet(axisIdToFormatter, config.id);
}

/**
 * @internal
 * Resolves the tick data for a single axis. Uses nice unit ticks when the axis
 * is linear with a unit-converting formatter; otherwise falls back to the
 * standard formatted-ticks path.
 */
export function getTicksForAxis(
  config: XYChartAxisInternal | undefined,
  numTicks: number,
  scale: AnyXYChartScale | undefined,
  formatter: ReturnType<typeof getFormatter> | undefined,
  availableSize: number,
  orientation: 'horizontal' | 'vertical',
): TickAxis {
  if (
    config &&
    !('categories' in config) &&
    shouldUseNiceUnitTicks(config) &&
    scale !== undefined &&
    formatter !== undefined
  ) {
    const domain = scale.domain() as [number, number];
    const niceTicks = computeNiceUnitTicks(
      domain,
      config.formatter,
      availableSize,
      orientation,
      formatter as FormatterWithPrecision,
      scale as ContinuousScale,
    );

    if (niceTicks !== null) {
      return niceTicks;
    }
  }

  return getTicksFormattedByConfig(
    config,
    numTicks,
    scale,
    formatter,
    orientation === 'horizontal' ? availableSize : undefined,
  );
}

export function XYChartAxesTicksProvider({
  children,
}: Readonly<PropsWithChildren>) {
  const { width, height } = useGridPlotSize();
  const axisIdToFormatter: AxisIdToFormatter = useAxesFormatter();
  const config = useVisibleAxesConfig();
  const scales = useVisibleAxesScales();
  const horizontalNumTicks = getHorizontalNumTicks(width);
  const verticalNumTicks = getVerticalNumTicks(height);
  const formatCategory = useFormatCategory();

  const topScale = scales.top;
  const topConfig = config.top;

  const topFormatter = topConfig
    ? getFormatter(
        horizontalNumTicks,
        axisIdToFormatter,
        topConfig,
        topScale,
        formatCategory,
      )
    : undefined;

  const topTicks = filterOutOfRangeTicks(
    getTicksForAxis(
      topConfig,
      horizontalNumTicks,
      topScale,
      topFormatter,
      width,
      'horizontal',
    ),
    topScale,
    width,
  );

  const bottomScale = scales.bottom;
  const bottomConfig = config.bottom;

  const bottomFormatter = bottomConfig
    ? getFormatter(
        horizontalNumTicks,
        axisIdToFormatter,
        bottomConfig,
        bottomScale,
        formatCategory,
      )
    : undefined;

  const bottomTicks = filterOutOfRangeTicks(
    getTicksForAxis(
      bottomConfig,
      horizontalNumTicks,
      bottomScale,
      bottomFormatter,
      width,
      'horizontal',
    ),
    bottomScale,
    width,
  );

  const leftScale = scales.left;
  const leftConfig = config.left;

  const leftFormatter = leftConfig
    ? getFormatter(
        verticalNumTicks,
        axisIdToFormatter,
        leftConfig,
        leftScale,
        formatCategory,
      )
    : undefined;

  const leftTicks = filterOutOfRangeTicks(
    getTicksForAxis(
      leftConfig,
      verticalNumTicks,
      leftScale,
      leftFormatter,
      height,
      'vertical',
    ),
    leftScale,
    height,
  );

  const rightScale = scales.right;
  const rightConfig = config.right;

  const rightFormatter = rightConfig
    ? getFormatter(
        verticalNumTicks,
        axisIdToFormatter,
        rightConfig,
        rightScale,
        formatCategory,
      )
    : undefined;

  const rightTicks = filterOutOfRangeTicks(
    getTicksForAxis(
      rightConfig,
      verticalNumTicks,
      rightScale,
      rightFormatter,
      height,
      'vertical',
    ),
    rightScale,
    height,
  );

  return (
    <XYChartAxesTicksContext.Provider
      value={{ topTicks, bottomTicks, leftTicks, rightTicks }}
    >
      {children}
    </XYChartAxesTicksContext.Provider>
  );
}
